import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * Users API using RTK Query
 *
 * Provides user management endpoints with automatic caching,
 * request deduplication, and loading state management.
 *
 * All endpoints require admin group membership.
 *
 * Uses centralized baseQuery for consistent error handling.
 */
export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery,
  tagTypes: ['Users', 'Accounts'],
  // Cache configuration
  keepUnusedDataFor: 300, // 5 minutes cache for user data
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    /**
     * Get list of accounts (superadmin only)
     */
    getAccounts: builder.query({
      query: () => '/api/v1/superadmin/accounts/',
      providesTags: ['Accounts']
    }),

    /**
     * Get list of users with pagination, search, and ordering
     * @param {number} page - Page number (default: 1)
     * @param {number} pageSize - Items per page (default: 20)
     * @param {string} search - Search query for email, first_name, last_name, phone
     * @param {string} ordering - Django ordering parameter (e.g., 'email' or '-email')
     * @param {string} accountId - Filter by account ID (superadmin only)
     */
    getUsers: builder.query({
      query: ({ page = 1, pageSize = 20, search = '', ordering = '', accountId = '', group = '', isSuperAdmin = false } = {}) => {
        const basePath = isSuperAdmin ? '/api/v1/superadmin/users/' : '/api/v1/users/';
        return `${basePath}?page=${page}&page_size=${pageSize}${search ? `&search=${search}` : ''}${ordering ? `&ordering=${ordering}` : ''}${accountId ? `&account=${accountId}` : ''}${group ? `&group=${group}` : ''}`;
      },
      // LIST/ITEM pattern: provide both individual item tags and list tag
      // Result structure: { count, next, previous, results: [...] }
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Users', id })),
              { type: 'Users', id: 'LIST' },
            ]
          : [{ type: 'Users', id: 'LIST' }],
    }),

    /**
     * Get single user by ID
     */
    getUserById: builder.query({
      query: (id) => `/api/v1/users/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Users', id }],
    }),

    /**
     * Create new user
     */
    createUser: builder.mutation({
      query: ({ email, firstName, lastName, phone, password, accountId }) => ({
        url: '/api/v1/users/',
        method: 'POST',
        body: {
          email,
          first_name: firstName,
          last_name: lastName,
          phone,
          password,
          account: accountId,
        },
      }),
      // Only invalidate the LIST tag
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),

    /**
     * Update existing user
     */
    updateUser: builder.mutation({
      query: ({ id, email, firstName, lastName, phone, accountId }) => ({
        url: `/api/v1/users/${id}/`,
        method: 'PUT',
        body: {
          email,
          first_name: firstName,
          last_name: lastName,
          phone,
          account: accountId,
        },
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, { id }) => [
        { type: 'Users', id },
        { type: 'Users', id: 'LIST' },
      ],
    }),

    /**
     * Delete user
     */
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `/api/v1/users/${id}/`,
        method: 'DELETE',
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, id) => [
        { type: 'Users', id },
        { type: 'Users', id: 'LIST' },
      ],
    }),

    /**
     * Get all available groups/roles
     */
    getGroups: builder.query({
      query: () => '/api/v1/groups/',
      providesTags: ['Groups'],
    }),

    /**
     * Update user groups
     */
    updateUserGroups: builder.mutation({
      query: ({ userId, groupIds }) => ({
        url: `/api/v1/users/${userId}/groups/`,
        method: 'PATCH',
        body: { group_ids: groupIds },
      }),
      // Invalidate the specific user and the list to refresh UI
      invalidatesTags: (result, error, { userId }) => [
        { type: 'Users', id: userId },
        { type: 'Users', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAccountsQuery,
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetGroupsQuery,
  useUpdateUserGroupsMutation,
} = usersApi;
